import { type FC, useState } from "react"

import { toSlug } from "@forento/core/utilities/string"
import { exhaustiveGuard } from "@forento/core/utilities/switch"

import Button, { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

const SlugModal: FC<{ isOpen: boolean; onClose(): void }> = ({ isOpen, onClose }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!

	const [slug, setSlug] = useState(platform.slug)

	const submitSlug = query.platform.setSlug.useMutation({
		onSuccess: async ({ status }) => {
			if (status === "taken") {
				await alert.show("Slug taken", "The slug is already taken by another academy.")
				return
			}
			if (status === "invalid") {
				await alert.show("Invalid slug", "The slug is invalid.")
				return
			}
			if (status !== "success") exhaustiveGuard(status)

			await user.reload()
			await alert.show("Slug updated", "Your slug has been updated.")
			setSlug("")
			onClose()
		},
		onError: async error => {
			console.error(error)
			await alert.show("Error", "An error occurred while updating your slug.")
		},
	})

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} onSubmit={() => submitSlug.mutate(slug)}>
			<ModalTitle>Update domain slug</ModalTitle>
			<ModalText>If you want to change your default domain you can update your slug here.</ModalText>
			<ModalInputs>
				<InputField
					label="Slug (x.forento.net, x.forento.app)"
					placeholder={platform.slug}
					value={slug}
					onChange={value => setSlug(toSlug(value))}
				/>
			</ModalInputs>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose} isDisabled={submitSlug.isPending}>
					Cancel
				</Button>
				<SubmitButton variant="primary" isLoading={submitSlug.isPending} isDisabled={slug.trim().length === 0}>
					Save
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

export default SlugModal
